import React from "react";
import {
  AnalyticsSection,
  StatCard,
  AreaChart,
  PieChart,
} from "./ChartComponents";
import { months, websiteData } from "./analyticsData";

const WebsiteAnalytics = () => {
  return (
    <div>
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <StatCard
          title="Total Visits"
          value={websiteData.totalVisits.toLocaleString()}
        />
        <StatCard
          title="Unique Visitors"
          value={websiteData.uniqueVisitors.toLocaleString()}
        />
        <StatCard
          title="Avg. Session Duration"
          value={websiteData.averageSessionDuration}
        />
        <StatCard title="Bounce Rate" value={websiteData.bounceRate} />
      </div>

      {/* Traffic Trend */}
      <AnalyticsSection title="Monthly visitor trend">
        <div className="mb-4">
          <p className="text-sm text-gray-500 mb-2">Unique visitors by month</p>

          <div className="mt-4">
            <AreaChart
              series={[websiteData.visitorTrend]}
              labels={months}
              height={300}
            />
          </div>

          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">
                Total Yearly Visitors
              </p>
              <p className="text-xl font-bold text-cpaGold">
                {websiteData.visitorTrend.data
                  .reduce((a, b) => a + b, 0)
                  .toLocaleString()}
              </p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Monthly Average</p>
              <p className="text-xl font-bold text-cpaGold">
                {Math.round(
                  websiteData.visitorTrend.data.reduce((a, b) => a + b, 0) / 12
                ).toLocaleString()}
              </p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Highest Month</p>
              <p className="text-xl font-bold text-cpaGold">
                {Math.max(...websiteData.visitorTrend.data).toLocaleString()}{" "}
                (Dec)
              </p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Growth (YTD)</p>
              <p className="text-xl font-bold text-cpaGold">
                {Math.round(
                  ((websiteData.visitorTrend.data[11] -
                    websiteData.visitorTrend.data[0]) /
                    websiteData.visitorTrend.data[0]) *
                    100
                )}
                %
              </p>
            </div>
          </div>
        </div>
      </AnalyticsSection>

      {/* Traffic Sources and Top Pages */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <AnalyticsSection title="Traffic sources">
          <PieChart
            segments={websiteData.trafficSources}
            height={450}
            segmentValueLabel="visitors"
          />
        </AnalyticsSection>

        <AnalyticsSection title="Top pages by visits">
          <div className="space-y-4">
            {websiteData.topPages.map((page, index) => (
              <div
                key={index}
                className="flex items-center p-4 bg-gray-50 rounded-lg"
              >
                <div className="w-8 h-8 rounded-full bg-cpaGold text-white flex items-center justify-center font-bold mr-4">
                  {index + 1}
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{page.page}</p>
                </div>
                <div className="text-right">
                  <p className="font-medium text-gray-900">
                    {page.visits.toLocaleString()} Total Visits
                  </p>
                  <p className="text-sm text-cpaGold">
                    {Math.round((page.visits / websiteData.totalVisits) * 100)}%
                    of traffic
                  </p>
                </div>
              </div>
            ))}
          </div>
        </AnalyticsSection>
      </div>

      {/* User Behavior and Devices */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <AnalyticsSection title="User behavior">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Session Duration
              </h3>
              <div className="space-y-3">
                {[
                  {
                    duration: "0-30 seconds",
                    percentage: "28%",
                    color: "#F8E3B5",
                  },
                  {
                    duration: "30-60 seconds",
                    percentage: "19%",
                    color: "#F0CA83",
                  },
                  {
                    duration: "1-3 minutes",
                    percentage: "32%",
                    color: "#E8B151",
                  },
                  {
                    duration: "3-10 minutes",
                    percentage: "15%",
                    color: "#E1A731",
                  },
                  {
                    duration: "10+ minutes",
                    percentage: "6%",
                    color: "#D4A82B",
                  },
                ].map((item, index) => (
                  <div key={index} className="space-y-1">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-700">{item.duration}</span>
                      <span className="font-medium">{item.percentage}</span>
                    </div>
                    <div className="w-full bg-gray-200 h-2 rounded-full">
                      <div
                        className="h-2 rounded-full"
                        style={{
                          width: item.percentage,
                          backgroundColor: item.color,
                        }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Pages Per Session
              </h3>
              <div className="space-y-3">
                {[
                  { pages: "1 page", percentage: "32%", color: "#F8E3B5" },
                  { pages: "2 pages", percentage: "25%", color: "#F0CA83" },
                  { pages: "3 pages", percentage: "18%", color: "#E8B151" },
                  { pages: "4 pages", percentage: "14%", color: "#E1A731" },
                  { pages: "5+ pages", percentage: "11%", color: "#D4A82B" },
                ].map((item, index) => (
                  <div key={index} className="space-y-1">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-700">{item.pages}</span>
                      <span className="font-medium">{item.percentage}</span>
                    </div>
                    <div className="w-full bg-gray-200 h-2 rounded-full">
                      <div
                        className="h-2 rounded-full"
                        style={{
                          width: item.percentage,
                          backgroundColor: item.color,
                        }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </AnalyticsSection>

        <AnalyticsSection title="Device breakdown">
          <div>
            <PieChart
              segments={[
                { name: "Mobile", value: 58, color: "#3B82F6" },
                { name: "Desktop", value: 35, color: "#10B981" },
                { name: "Tablet", value: 7, color: "#F59E0B" },
              ]}
              height={250}
              segmentValueLabel="percent"
            />

            <div className="mt-6 grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-3xl font-bold text-blue-600">58%</p>
                <p className="text-sm text-gray-500 mt-1">Mobile</p>
                <p className="text-xs text-gray-400 mt-1">+4% vs last year</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-3xl font-bold text-green-600">35%</p>
                <p className="text-sm text-gray-500 mt-1">Desktop</p>
                <p className="text-xs text-gray-400 mt-1">-3% vs last year</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-3xl font-bold text-amber-600">7%</p>
                <p className="text-sm text-gray-500 mt-1">Tablet</p>
                <p className="text-xs text-gray-400 mt-1">-1% vs last year</p>
              </div>
            </div>
          </div>
        </AnalyticsSection>
      </div>
    </div>
  );
};

export default WebsiteAnalytics;
